import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyS, BrandS, LabelL } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { Position } from '@sorare/core/src/components/CornerBadge';
import { isType } from '@sorare/core/src/lib/gql';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { DetailsDialog } from 'components/missions/Streaks/DetailsDialog';

import { DumbTask } from '../DumbTask';
import { TaskCampaignLabel } from '../Streaks/TaskCampaignLabel';
import { Colors } from '../getTaskColors';
import { StreakTaskFooter } from './StreakTaskFooter';
import type { StreakTask_taskInterface } from './__generated__/index.graphql';

const Root = styled.div`
  position: relative;
  width: 100%;
`;
const Footer = styled(Vertical).attrs({ center: true })`
  position: relative;
  flex: 1;
`;
const RewardTaskCampaignWrapper = styled(Vertical)`
  position: absolute;
  top: -2px;
  left: calc(-1 * var(--half-unit));
`;
const RewardTaskCampaignWrapperRight = styled(Vertical)`
  position: absolute;
  top: -2px;
  right: 0;
`;
const Img = styled.img`
  width: var(--triple-unit);
  @media ${laptopAndAbove} {
    width: var(--quadruple-unit);
  }
`;
const Threshold = styled(BrandS)`
  text-wrap: nowrap;
  &.lowered {
    text-shadow: 0px 0px 8px rgba(247, 177, 0, 0.58);
  }
  &.disabled {
    opacity: 0.5;
    text-decoration-line: line-through;
    text-decoration-color: #f7b100;
  }
`;
const TitleWrapper = styled(SBHorizontal)`
  width: 100%;
`;

type Props = {
  streakTask: StreakTask_taskInterface;
  featured?: boolean;
};
export const StreakTask = ({ streakTask, featured }: Props) => {
  if (!isType(streakTask, 'ThresholdsStreakTask')) {
    return null;
  }

  const {
    templateIconUrl,
    iconUrl,
    title,
    description,
    scoreTaskCampaign,
    rewardTaskCampaign,
    currentThreshold,
    engaged,
  } = streakTask;

  return (
    <Root>
      {!!scoreTaskCampaign && (
        <RewardTaskCampaignWrapper>
          <TaskCampaignLabel taskCampaign={scoreTaskCampaign} />
        </RewardTaskCampaignWrapper>
      )}
      {!!rewardTaskCampaign && !engaged && (
        <RewardTaskCampaignWrapperRight>
          <TaskCampaignLabel
            taskCampaign={rewardTaskCampaign}
            align={Position.RIGHT}
          />
        </RewardTaskCampaignWrapperRight>
      )}
      <DumbTask
        color={engaged ? Colors.RED : undefined}
        featured={featured}
        icon={!!templateIconUrl && <Img src={templateIconUrl} alt="" />}
        title={
          <TitleWrapper>
            <LabelL bold>{title}</LabelL>
            {!!iconUrl && <img width={20} src={iconUrl} alt="" />}
            {!!currentThreshold && (
              <Horizontal gap={0.5}>
                <Threshold
                  className={classNames('text-gradient-color', {
                    lowered: !!currentThreshold.score,
                  })}
                  style={
                    {
                      '--text-gradient-color': `var(--c-gradient-streaks)`,
                    } as CSSProperties
                  }
                >
                  {currentThreshold.score}
                </Threshold>
                {!!currentThreshold.originalScore && (
                  <Threshold className="disabled">
                    <span
                      className="text-gradient-color"
                      style={
                        {
                          '--text-gradient-color': `var(--c-gradient-streaks)`,
                        } as CSSProperties
                      }
                    >
                      {currentThreshold.originalScore}
                    </span>
                  </Threshold>
                )}
              </Horizontal>
            )}
          </TitleWrapper>
        }
        description={
          !!engaged && (
            <BodyS>
              <Ellipsis visibleLines={2}>{description}</Ellipsis>
            </BodyS>
          )
        }
        footer={
          <Footer>
            {!!rewardTaskCampaign && !!engaged && (
              <RewardTaskCampaignWrapper>
                <TaskCampaignLabel taskCampaign={rewardTaskCampaign} />
              </RewardTaskCampaignWrapper>
            )}
            <StreakTaskFooter streakTask={streakTask} />
          </Footer>
        }
        detailsDialog={({ open, onClose }) =>
          open && (
            <DetailsDialog thresholdStreakTask={streakTask} onClose={onClose} />
          )
        }
      />
    </Root>
  );
};

StreakTask.fragments = {
  taskInterface: gql`
    fragment StreakTask_taskInterface on TaskInterface {
      id
      iconUrl
      templateIconUrl
      title
      description
      ... on ThresholdsStreakTask {
        id
        engaged
        currentThreshold {
          originalScore
          score
        }
        rewardTaskCampaign {
          slug
          ...TaskCampaignLabel_taskCampaign
        }
        scoreTaskCampaign {
          slug
          ...TaskCampaignLabel_taskCampaign
        }
      }
      ...StreakTaskFooter_taskInterface
      ...DetailsDialog_thresholdStreakTask
    }
    ${StreakTaskFooter.fragments.taskInterface}
    ${DetailsDialog.fragments.thresholdStreakTask}
    ${TaskCampaignLabel.fragments.taskCampaign}
  ` as TypedDocumentNode<StreakTask_taskInterface>,
};
